import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EventViewEntityListPageContent } from 'components/eventView/pages/EVEntityListPageContent';
import { EventViewEntityPageContent } from 'components/eventView/pages/EVEntityPageContent';
import { motion } from 'framer-motion';
import { AnimTap, EColors2, EFonts } from 'libs/styles/styling';
import { useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CtxEventView } from 'session/ctxEventView';
import styled from 'styled-components/macro';

interface Props {
    detailId?: string;
    detailList?: {
        entity: 'program';
        entityId: string;
    };
    className?: string;
}

export const EVDetailPagePC = (props: Props) => {
    const detailId = props.detailId;
    const ctxEventView = useContext(CtxEventView);
    const [searchParams, setSearchParams] = useSearchParams();
    const scrollContainerRef = useRef<HTMLDivElement>();
    const { allStories } = ctxEventView;
    const detail = allStories.find((e) => e.storyId === detailId);
    const eventInfo = ctxEventView.eventData.eventInfo;

    const getTitle = () => {
        if (props.detailId) {
            return detail?.storyHeader?.title;
        } else if (props.detailList.entity === 'program') {
            return ctxEventView.allPrograms?.find((e) => e.programId === props.detailList.entityId)
                ?.title;
        }
    };

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
    }, [scrollContainerRef.current, props.detailId]);

    if (!detail && !props.detailList) {
        return;
    }

    return (
        <S_EVDetailPagePC
            ref={scrollContainerRef}
            className={props.className}
            initial={{ x: '100%' }}
            animate={{ x: '0%' }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeIn' }}
            $background={eventInfo.headerImageUrl}
        >
            <S_TitleHeader>
                <S_Title>{getTitle()}</S_Title>
                <S_Close
                    {...AnimTap}
                    onClick={() => {
                        if (props.detailId) {
                            searchParams.delete('detailId');
                            searchParams.delete('markerId');
                        }
                        if (props.detailList) {
                            searchParams.delete('detailEntityType');
                            searchParams.delete('detailEntityId');
                        }
                        setSearchParams(searchParams);
                    }}
                >
                    <S_FontAwesomeIcon icon={['far', 'times']} />
                </S_Close>
            </S_TitleHeader>
            {props.detailId && <EventViewEntityPageContent detailId={props.detailId} />}
            {props.detailList && (
                <>
                    <S_EventViewEntityListPageContent
                        entity={props.detailList.entity}
                        entityId={props.detailList.entityId}
                        entityListAlign="center"
                    />
                </>
            )}
        </S_EVDetailPagePC>
    );
};

const S_EVDetailPagePC = styled(motion.div)<{ $background: string }>`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    height: 100vh;
    width: 530px;
    overflow-y: auto;
    overflow-x: hidden;
    background: #f6f9ff;
    box-shadow: -20px 9.002px 45.012px 0px rgba(0, 0, 0, 0.1);
    z-index: 300;
`;

const S_Close = styled(motion.div)`
    position: absolute;
    right: 20px;
    top: 14px;
    width: 53px;
    height: 53px;
    border-radius: 50%;
    background-color: ${EColors2.DARK};
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`;

const S_FontAwesomeIcon = styled(FontAwesomeIcon)`
    color: white;
    font-size: 20px;
`;

const S_TitleHeader = styled.div`
    background: ${EColors2.GREY2};
    display: flex;
    background-size: cover;
    align-items: center;
    text-align: left;
    height: 80px;
    width: 100%;
    position: sticky;
    top: 0;
    z-index: 100;
`;

const S_Title = styled.div`
    padding: 0px 0px 0px 35px;
    font-family: ${EFonts.Bold};
    font-size: 18px;
    line-height: 25px;
    line-height: normal;
    color: ${EColors2.DARK};
    width: 350px;
`;

const S_EventViewEntityListPageContent = styled(EventViewEntityListPageContent)`
    padding: 20px 0;
`;
